<template>
    <component
        v-if="isRouterActive"
        :is="linkComponentType"
        v-bind="routerLinkScope"
        v-slot="{ href, navigate, isExactActive }"
        custom
    >
        <li role="tab" :class="[$style['mds-tabs__item'], classObject, activeClass(isExactActive)]">
            <mds-button
                :href="href"
                :on-dark="onDark"
                :icon="icon"
                :icon-right="iconRight"
                :size="size"
                :text="text"
                variation="flat"
                :type="typeAttribute"
                :target="target"
                @click="navigate"
            >
                <slot>{{ text }}</slot>
            </mds-button>
        </li>
    </component>
    <li v-else role="tab" :class="[$style['mds-tabs__item'], classObject]">
        <mds-button
            :href="href"
            :on-dark="onDark"
            :icon="icon"
            :icon-right="iconRight"
            :size="size"
            :text="text"
            variation="flat"
            :type="typeAttribute"
            :target="target"
            :disabled="disabledAttribute"
        >
            <slot>{{ text }}</slot>
        </mds-button>
    </li>
</template>

<script>
    import { MdsButton } from '@mds/button-vue3';
    import { routerLinkProps, useRouting, getLinkTag } from '@mds/routable-vue3';
    import { computed } from 'vue';

    export default {
        name: 'MdsTabsItem',
        components: {
            MdsButton,
        },
        props: {
            active: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, applies active styling to the tab item.',
                },
            },
            href: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'When provided, makes the tabs item `a`-based and sets the `href` path.',
                },
            },
            disabled: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: "If `true`, disables the tab item. Note: This shouldn't be used if `href` is used.",
                },
            },
            icon: {
                type: String,
                docs: {
                    validation: 'Must be a valid icon name.',
                    description: 'Sets the left icon.',
                },
            },
            iconRight: {
                type: String,
                docs: {
                    validation: 'Must be a valid icon name.',
                    description: 'Sets the right icon.',
                },
            },
            target: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the `target` attribute.',
                },
            },
            text: {
                type: String,
                docs: {
                    validation: '**Required**',
                    description: 'Sets the tab item text.',
                },
            },
            ...routerLinkProps,
        },
        setup(props) {
            const { isRouterActive, routerLinkScope } = useRouting(props);
            let linkComponentType = computed(() => {
                const linkElement = getLinkTag(props.linkTag);

                return linkElement;
            });

            return {
                isRouterActive,
                routerLinkScope,
                linkComponentType,
            };
        },

        inject: ['onDark', 'size'],
        computed: {
            classObject() {
                const obj = {};
                obj[this.$style['mds-tabs__item--active']] = this.active;

                return obj;
            },
            typeAttribute() {
                return this.href ? null : 'button';
            },
            disabledAttribute() {
                return this.typeAttribute === 'button' && this.disabled ? 'disabled' : null;
            },
        },
        methods: {
            activeClass(isExactActive) {
                return isExactActive ? this.$style['mds-tabs__item--active'] : null;
            },
        },
    };
</script>

<style lang="scss" module>
    @use './tabs.scss';
</style>
